import { useNavigate } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { useEffect, useState, useRef, useCallback } from "react";
import Api from "../Auth/Api";
import { Toast, Tab, Tabs, Modal } from "react-bootstrap";
import { decodeJWT } from "../Components/jwtUtils";
import CargosManager from "../Components/Ajustes/CargosManager";
import PermissoesGestorTab from "../Components/Ajustes/PermissoesGestorTab";
import UnidadesManager from "../Components/Ajustes/UnidadesManager";
import ImageCardUpload from "../Components/Ajustes/ImageCardUpload";
import PfxCardUpload from "../Components/Ajustes/PfxCardUpload";
import { useAjustes } from "../Context/AjustesContext";

// Modelo padrão exibido como placeholder quando a empresa não tem texto próprio.
const TEXTO_CERTIFICADO_PLACEHOLDER =
    "Certificamos que {aluno}, identificado(a) com o CPF {cpf}, concluiu com aproveitamento satisfatório o Curso sobre {curso}, promovida nas dependências da empresa {empresa} na unidade de {unidade} na data {data}, conforme exigências da {topico}, com carga horária de {carga_horaria}.";

// Marcadores disponíveis no editor de texto do certificado.
const MARCADORES_CERTIFICADO = [
    { tag: '{aluno}', desc: 'Nome do aluno' },
    { tag: '{cpf}', desc: 'CPF do aluno' },
    { tag: '{curso}', desc: 'Nome do curso' },
    { tag: '{empresa}', desc: 'Nome da empresa' },
    { tag: '{unidade}', desc: 'Unidade do aluno' },
    { tag: '{data}', desc: 'Data de emissão' },
    { tag: '{topico}', desc: 'Tópico/tema' },
    { tag: '{carga_horaria}', desc: 'Carga horária' },
];

function Ajustes() {
    const navigate = useNavigate();
    const token = JSON.parse(localStorage.getItem("user_token"));
    const decoded = decodeJWT(token);
    const nivel = decoded.nivel;
    const isSuperAdmin = decoded.super_admin === true;
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [activeTab, setActiveTab] = useState("permissoes");
    const [showModalRemoverDiploma, setShowModalRemoverDiploma] = useState(false);
    const { recarregarAjustes } = useAjustes();

    // Imagem do certificado (diploma) — por empresa.
    const [diplomaUrl, setDiplomaUrl] = useState(null);
    const [diplomaSelecionado, setDiplomaSelecionado] = useState(false);
    const diplomaRef = useRef(null);

    // Assinatura digital (.pfx) — por empresa (gerenciada pelo Super Admin).
    const [temAssinatura, setTemAssinatura] = useState(false);
    const [assinaturaNome, setAssinaturaNome] = useState(null);
    const [assinaturaValidade, setAssinaturaValidade] = useState(null);
    const [assinaturaSelecionada, setAssinaturaSelecionada] = useState(false);
    const [showModalRemoverAssinatura, setShowModalRemoverAssinatura] = useState(false);
    const assinaturaRef = useRef(null);

    const { register, handleSubmit, setValue, control } = useForm();

    const showToastMessage = useCallback((header, body, variant = 'error') => {
        const type = variant === 'success' ? 'toast-success' : variant === 'danger' ? 'toast-error' : 'toast-' + variant;
        setToast({ header, body, type });
        setShowToast(true);
    }, []);

    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);

    // Estilos de hover dos cards de imagem
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .ajustes-image-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
            .ajustes-image-card:hover { transform: translateY(-4px); box-shadow: 0 8px 16px rgba(0,0,0,0.12) !important; }
            .ajustes-upload-label { transition: all 0.2s ease; }
            .ajustes-upload-label:hover { transform: scale(1.01); box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
            .ajustes-icon-circle { transition: all 0.3s ease; }
            .ajustes-image-card:hover .ajustes-icon-circle { transform: scale(1.1) rotate(5deg); }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    // Verificar se o usuário é administrador
    useEffect(() => {
        if (nivel !== 'Administrador') {
            navigate('/sem-permissao');
        }
    }, [nivel, navigate]);

    // Carregar ajustes (flags de gestor + certificado) ao montar
    const carregarAjustes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await Api.get('api/ajustes', {
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = response.data;
            setValue('gestor_pode_cadastrar_editar_usuario', Number(data.gestor_pode_cadastrar_editar_usuario ?? 1) !== 0);
            setValue('gestor_pode_atribuir_cursos', Number(data.gestor_pode_atribuir_cursos ?? 1) !== 0);
            setValue('gestor_pode_resetar_senha', Number(data.gestor_pode_resetar_senha ?? 1) !== 0);
            setValue('gestor_escopo_edicao', data.gestor_escopo_edicao || 'responsavel');
            setValue('gestor_escopo_reset_senha', data.gestor_escopo_reset_senha || 'responsavel');
            setValue('gestor_ver_todos_cursos', Number(data.gestor_ver_todos_cursos ?? 1) !== 0);
            setValue('gestor_pode_criar_agendamento', Number(data.gestor_pode_criar_agendamento ?? 0) !== 0);
            setValue('rh_pode_criar_agendamento', Number(data.rh_pode_criar_agendamento ?? 0) !== 0);
            setValue('gestor_agenda_todas_unidades', Number(data.gestor_agenda_todas_unidades ?? 0) !== 0);
            setValue('rh_agenda_todas_unidades', Number(data.rh_agenda_todas_unidades ?? 0) !== 0);
            setValue('gestor_pode_importar_usuario', Number(data.gestor_pode_importar_usuario ?? 0) !== 0);
            setValue('rh_pode_importar_usuario', Number(data.rh_pode_importar_usuario ?? 0) !== 0);
            setValue('gestor_pode_exportar_usuario', Number(data.gestor_pode_exportar_usuario ?? 1) !== 0);
            setValue('rh_pode_exportar_usuario', Number(data.rh_pode_exportar_usuario ?? 1) !== 0);
            setValue('texto_certificado', data.texto_certificado || '');
            setDiplomaUrl(data.diploma ? getImageUrl(data.diploma) : null);
            setDiplomaSelecionado(false);
            setTemAssinatura(!!data.tem_assinatura);
            setAssinaturaNome(data.assinatura_nome || null);
            setAssinaturaValidade(data.assinatura_validade || null);
            setAssinaturaSelecionada(false);
            setValue('assinatura_senha', '');
        } catch (error) {
            console.error("Erro ao carregar ajustes:", error);
        } finally {
            setLoading(false);
        }
    }, [token, setValue, getImageUrl]);

    useEffect(() => {
        carregarAjustes();
    }, [carregarAjustes]);

    const onSubmit = async (data) => {
        try {
            setSalvando(true);

            const formData = new FormData();
            formData.append('gestor_pode_cadastrar_editar_usuario', data.gestor_pode_cadastrar_editar_usuario ? '1' : '0');
            // Atribuir cursos depende da edição: com a edição desligada a opção some e é gravada desligada.
            formData.append('gestor_pode_atribuir_cursos', data.gestor_pode_cadastrar_editar_usuario && data.gestor_pode_atribuir_cursos ? '1' : '0');
            formData.append('gestor_pode_resetar_senha', data.gestor_pode_resetar_senha ? '1' : '0');
            formData.append('gestor_escopo_edicao', data.gestor_escopo_edicao || 'responsavel');
            formData.append('gestor_escopo_reset_senha', data.gestor_escopo_reset_senha || 'responsavel');
            formData.append('gestor_ver_todos_cursos', data.gestor_ver_todos_cursos ? '1' : '0');
            formData.append('gestor_pode_criar_agendamento', data.gestor_pode_criar_agendamento ? '1' : '0');
            formData.append('rh_pode_criar_agendamento', data.rh_pode_criar_agendamento ? '1' : '0');
            formData.append('gestor_agenda_todas_unidades', data.gestor_agenda_todas_unidades ? '1' : '0');
            formData.append('rh_agenda_todas_unidades', data.rh_agenda_todas_unidades ? '1' : '0');
            formData.append('gestor_pode_importar_usuario', data.gestor_pode_importar_usuario ? '1' : '0');
            formData.append('rh_pode_importar_usuario', data.rh_pode_importar_usuario ? '1' : '0');
            formData.append('gestor_pode_exportar_usuario', data.gestor_pode_exportar_usuario ? '1' : '0');
            formData.append('rh_pode_exportar_usuario', data.rh_pode_exportar_usuario ? '1' : '0');
            formData.append('texto_certificado', data.texto_certificado ?? '');
            if (data.diploma && data.diploma instanceof FileList && data.diploma.length > 0) {
                formData.append('diploma', data.diploma[0]);
            }
            if (data.assinatura && data.assinatura instanceof FileList && data.assinatura.length > 0) {
                formData.append('assinatura', data.assinatura[0]);
            }
            if (data.assinatura_senha) {
                formData.append('assinatura_senha', data.assinatura_senha);
            }

            await Api.post('api/ajustes', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            await recarregarAjustes();
            await carregarAjustes();
            setSalvando(false);

            setToast({ header: 'Sucesso!', body: 'Ajustes salvos com sucesso.', type: 'toast-success' });
            setShowToast(true);

        } catch (error) {
            setSalvando(false);
            setToast({
                header: 'Erro!',
                body: error.response?.data?.message || 'Erro ao atualizar ajustes da plataforma',
                type: 'toast-error'
            });
            setShowToast(true);
        }
    };

    const handleRemoverDiploma = () => {
        setShowModalRemoverDiploma(true);
    };

    const confirmarRemoverDiploma = async () => {
        setShowModalRemoverDiploma(false);
        try {
            const response = await Api.delete('api/ajustes/arquivo/diploma', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDiplomaSelecionado(false);
            await carregarAjustes();
            await recarregarAjustes();
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowToast(true);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao remover a imagem do certificado', type: 'toast-error' });
            setShowToast(true);
        }
    };

    const handleRemoverAssinatura = () => {
        setShowModalRemoverAssinatura(true);
    };

    const confirmarRemoverAssinatura = async () => {
        setShowModalRemoverAssinatura(false);
        try {
            const response = await Api.delete('api/ajustes/arquivo/assinatura', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssinaturaSelecionada(false);
            await carregarAjustes();
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowToast(true);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao remover a assinatura digital', type: 'toast-error' });
            setShowToast(true);
        }
    };

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mb-5">

                <div className="card shadow-sm border-0">
                    <div className="card-header bg-primary text-white py-3">
                        <h5 className="mb-0"><i className="bi bi-gear-wide-connected me-2"></i>Ajustes da Plataforma</h5>
                    </div>
                    <div className="card-body p-3 p-md-4">
                        {loading ? (
                            <div className="d-flex justify-content-center my-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        ) : (
                            <form
                                onSubmit={(e) => {
                                    if (activeTab === 'cargos' || activeTab === 'unidades') {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleSubmit(onSubmit)(e);
                                }}
                            >
                                {/* Mobile: seletor de seção no lugar das abas */}
                                <div className="d-md-none mb-3">
                                    <label className="form-label text-primary fw-medium small mb-1">Seção</label>
                                    <select
                                        className="form-select"
                                        value={activeTab}
                                        onChange={(e) => setActiveTab(e.target.value)}
                                    >
                                        <option value="cargos">Cargos</option>
                                        <option value="unidades">Unidades</option>
                                        <option value="certificado">Certificado</option>
                                        {isSuperAdmin && <option value="assinatura">Assinatura Digital</option>}
                                        <option value="permissoes">Permissões</option>
                                    </select>
                                </div>

                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k)}
                                    className="mb-4 d-none d-md-flex"
                                >

                                    <Tab eventKey="cargos" title={<><i className="bi bi-briefcase me-2"></i>Cargos</>}>
                                        <CargosManager token={token} onToast={showToastMessage} />
                                    </Tab>

                                    <Tab eventKey="unidades" title={<><i className="bi bi-diagram-3 me-2"></i>Unidades</>}>
                                        <UnidadesManager token={token} onToast={showToastMessage} />
                                    </Tab>

                                    <Tab eventKey="certificado" title={<><i className="bi bi-award me-2"></i>Certificado</>}>
                                        <div className="row mt-4">
                                            {/* Imagem do certificado */}
                                            <ImageCardUpload
                                                campo="diploma"
                                                label="Imagem do Certificado"
                                                icon="bi-award"
                                                iconBg="bg-success bg-opacity-10"
                                                iconColor="text-success"
                                                valor={diplomaUrl}
                                                selecionado={diplomaSelecionado}
                                                inputRef={diplomaRef}
                                                control={control}
                                                onSelect={(campo, sel) => setDiplomaSelecionado(sel)}
                                                onRemove={handleRemoverDiploma}
                                                colClass="col-md-4 mb-4"
                                            />

                                            {/* Texto do certificado */}
                                            <div className="col-md-8 mb-4">
                                                <label className="form-label fw-bold">Texto do Certificado</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={6}
                                                    placeholder={TEXTO_CERTIFICADO_PLACEHOLDER}
                                                    {...register('texto_certificado')}
                                                ></textarea>
                                                <small className="text-muted d-block mt-1">
                                                    Deixe em branco para usar o modelo padrão do sistema.
                                                </small>

                                                <div className="mt-3 p-3 rounded bg-light border">
                                                    <div className="fw-semibold mb-2 small text-uppercase text-muted">
                                                        <i className="bi bi-braces me-1"></i>Marcadores disponíveis
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {MARCADORES_CERTIFICADO.map((m) => (
                                                            <span key={m.tag} className="badge bg-secondary bg-opacity-10 text-dark border" title={m.desc}>
                                                                <code className="text-light">{m.tag}</code> — {m.desc}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>

                                    {isSuperAdmin && (
                                        <Tab eventKey="assinatura" title={<><i className="bi bi-pen me-2"></i>Assinatura Digital</>}>
                                            <div className="row mt-4">
                                                <div className="col-md-5 mb-4">
                                                    <PfxCardUpload
                                                        temAssinatura={temAssinatura}
                                                        nome={assinaturaNome}
                                                        validade={assinaturaValidade}
                                                        selecionado={assinaturaSelecionada}
                                                        inputRef={assinaturaRef}
                                                        control={control}
                                                        register={register}
                                                        onSelect={(sel) => setAssinaturaSelecionada(sel)}
                                                        onRemove={handleRemoverAssinatura}
                                                    />
                                                </div>
                                                <div className="col-md-7 mb-4">
                                                    <div className="p-3 rounded bg-light border h-100">
                                                        <div className="fw-semibold mb-2"><i className="bi bi-info-circle me-2"></i>Sobre a assinatura digital</div>
                                                        <p className="small text-muted mb-2">
                                                            Envie o certificado <strong>.pfx</strong> (PKCS#12) da empresa e a respectiva senha.
                                                            A partir daí, todo certificado emitido (ou recriado) por esta empresa será
                                                            <strong> assinado digitalmente</strong> de forma criptográfica (PKCS#7), validável no Adobe Reader.
                                                        </p>
                                                        <ul className="small text-muted mb-0">
                                                            <li>O arquivo é armazenado de forma protegida e nunca é exposto publicamente.</li>
                                                            <li>A senha é guardada cifrada e nunca é retornada pela API.</li>
                                                            <li>Empresas sem certificado configurado continuam emitindo certificados sem assinatura.</li>
                                                            <li>A confiança jurídica (ex.: ICP-Brasil) depende do certificado fornecido.</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </Tab>
                                    )}

                                    <Tab eventKey="permissoes" title={<><i className="bi bi-shield-lock me-2"></i>Permissões</>}>
                                        <PermissoesGestorTab control={control} />
                                    </Tab>
                                </Tabs>

                                {activeTab !== 'cargos' && activeTab !== 'unidades' && (
                                    <div className="d-grid d-md-flex justify-content-md-end mt-4">
                                        <button type="submit" className="btn btn-primary" disabled={salvando}>
                                            {salvando ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Salvando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-save me-2"></i>
                                                    Salvar Ajustes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
            <Modal size='lg' show={showModalRemoverDiploma} onHide={() => setShowModalRemoverDiploma(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Remover imagem do certificado</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="body">
                                    <div className="d-flex">
                                        <div className="d-flex flex-column w-100">
                                            <div className="row">
                                                <div className="col-12">
                                                    <p>Tem certeza que deseja remover a imagem do certificado?</p>
                                                    <p>Essa ação não poderá ser revertida.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalRemoverDiploma(false)}>Cancelar</button>
                        <button type="button" className="btn btn-danger" onClick={confirmarRemoverDiploma}>Confirmar</button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal size='lg' show={showModalRemoverAssinatura} onHide={() => setShowModalRemoverAssinatura(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Remover assinatura digital</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="body">
                                    <div className="d-flex">
                                        <div className="d-flex flex-column w-100">
                                            <div className="row">
                                                <div className="col-12">
                                                    <p>Tem certeza que deseja remover a assinatura digital desta empresa?</p>
                                                    <p>Os próximos certificados emitidos sairão <b>sem assinatura</b>. Essa ação não poderá ser revertida.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalRemoverAssinatura(false)}>Cancelar</button>
                        <button type="button" className="btn btn-danger" onClick={confirmarRemoverAssinatura}>Confirmar</button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Ajustes;
